import AutoTable from 'components/AutoTable'
import React, { useMemo, useCallback } from 'react'
import moment from 'moment'
import { Alert } from 'reactstrap'
import { Link, useHistory } from 'react-router-dom'
import { useDispatch } from 'react-redux'

import { NetSuiteAsset } from 'types'
import { fetchSetupAdminUserId } from 'store/css-admin/userManager/operations'

interface LicenseResultsGridProps {
  netSuiteAssets: NetSuiteAsset[]
  selectNetSuiteAssets: (netsuiteAssetId: string) => void
}

const LicenseResultsGrid: React.FC<LicenseResultsGridProps> = ({
  netSuiteAssets = [],
  selectNetSuiteAssets,
}) => {
  const dispatch = useDispatch()
  const history = useHistory()
  const columns = useMemo(
    () => [
      {
        title: 'NS Customer Id',
        fieldValue: `managerNetSuiteCustomerId`,
      },
      {
        title: 'School',
        fieldValue: (row: NetSuiteAsset) => {
          const { site } = row || {}
          const { id, name } = site || {}
          return (
            <Link
              to={`/css-admin/sites/${id}`}
              onClick={e => {
                e.stopPropagation()
              }}
            >
              {name}
            </Link>
          )
        },
      },
      {
        title: 'Setup Admin',
        fieldValue: (row: NetSuiteAsset) => {
          const { adminEmail } = row || {}
          if (adminEmail) {
            return (
              <Link
                to="#"
                onClick={async e => {
                  e.stopPropagation()
                  e.preventDefault()
                  try {
                    const userId = await dispatch(
                      fetchSetupAdminUserId(adminEmail),
                    )
                    if (userId) {
                      history.push(`/css-admin/users/${adminEmail}/${userId}`)
                    }
                  } catch (error) {
                    // Error fetching setup admin user ID - silently continue
                  }
                }}
              >
                {adminEmail}
              </Link>
            )
          } else {
            return adminEmail
          }
        },
        sortValue: 'adminEmail',
        filterValue: 'adminEmail',
      },
      {
        title: 'NS Contract Line Id',
        fieldValue: (row: NetSuiteAsset) => {
          return (
            <Link
              to={`/css-admin/contractLine/${row.contractLineNSId}`}
              onClick={e => {
                e.stopPropagation()
              }}
            >
              {row.contractLineNSId}
            </Link>
          )
        },
        sortValue: 'contractLineNSId',
        filterValue: 'contractLineNSId',
      },
      {
        title: 'NS License Id',
        fieldValue: (row: NetSuiteAsset) => {
          return <Link to={`/css-admin/licenses/${row.id}`}>{row.nsName}</Link>
        },
        sortValue: 'id',
        filterValue: 'id',
      },
      {
        title: 'Expiration',
        fieldValue: (row: NetSuiteAsset) => {
          if (row.expirationDate) {
            return moment(row.expirationDate).format('YYYY-MM-DD')
          } else return ''
        },
      },
    ],
    [dispatch, history],
  )

  const handleRowClick = useCallback(
    (license: NetSuiteAsset) => {
      selectNetSuiteAssets(license.id.toString())
    },
    [selectNetSuiteAssets],
  )

  return netSuiteAssets.length ? (
    <AutoTable
      borderless
      columns={columns}
      filter
      headThemeLight
      rows={netSuiteAssets}
      sort
      rowOnClick={handleRowClick}
    />
  ) : (
    <Alert color="warning">No results found</Alert>
  )
}

export default LicenseResultsGrid
